import Dexie from 'dexie';
import StorageService from '../StorageService.js';

// Single Dexie instance for the app
export const db = new Dexie('sip_db');

// v1: initial initiatives + meta
db.version(1).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key'
});

// v2: telemetry (usage + feedback + sessions)
db.version(2).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs'
});

// v3: OKR primitives (objectives, key results, links)
db.version(3).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs',
  objectives: 'id, owner, status, progress, updatedAt',
  key_results: 'id, objectiveId, updatedAt',
  initiative_links: '++id, initiativeId, objectiveId, keyResultId'
});

// v4: dependencies and risks (for metrics engine enrichment)
db.version(4).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs',
  objectives: 'id, owner, status, progress, updatedAt',
  key_results: 'id, objectiveId, updatedAt',
  initiative_links: '++id, initiativeId, objectiveId, keyResultId',
  dependencies: '++id, fromId, toId, type', // type: depends_on | blocks | relates
  risks: '++id, initiativeId, level, impact, likelihood, updatedAt'
});

// v5: causality edges (local-only for Phase 0)
db.version(5).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs',
  objectives: 'id, owner, status, progress, updatedAt',
  key_results: 'id, objectiveId, updatedAt',
  initiative_links: '++id, initiativeId, objectiveId, keyResultId',
  dependencies: '++id, fromId, toId, type',
  risks: '++id, initiativeId, level, impact, likelihood, updatedAt',
  // Causal loop diagram edges (always use externalId references for nodes)
  causal_edges: '++id, fromExternalId, toExternalId, type, polarity, strength, evidenceLevel, confidence, lastValidated, owner'
});

// v6: Impact Mapping primitives (stakeholders, behaviors, deliverables)
db.version(6).stores({
  initiatives: 'id, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs',
  objectives: 'id, owner, status, progress, updatedAt',
  key_results: 'id, objectiveId, updatedAt',
  initiative_links: '++id, initiativeId, objectiveId, keyResultId',
  dependencies: '++id, fromId, toId, type',
  risks: '++id, initiativeId, level, impact, likelihood, updatedAt',
  causal_edges: '++id, fromExternalId, toExternalId, type, polarity, strength, evidenceLevel, confidence, lastValidated, owner',
  stakeholders: 'id, name, type, segment, owner, createdAt, updatedAt',
  behaviors: 'id, title, owner, createdAt, updatedAt',
  deliverables: 'id, title, owner, createdAt, updatedAt'
});

// v7: add deliverable field to initiatives (free-text output)
db.version(7).stores({
  initiatives: 'id, deliverable, owner, status, priority, strategicFit, createdAt, updatedAt',
  meta: 'key',
  usage_events: '++id, type, tabId, actionKey, promptKey, ts',
  message_feedback: '++id, messageId, promptKey, role, tabId, ts',
  app_feedback: '++id, category, ts',
  sessions: '++id, startTs, endTs',
  objectives: 'id, owner, status, progress, updatedAt',
  key_results: 'id, objectiveId, updatedAt',
  initiative_links: '++id, initiativeId, objectiveId, keyResultId',
  dependencies: '++id, fromId, toId, type',
  risks: '++id, initiativeId, level, impact, likelihood, updatedAt',
  causal_edges: '++id, fromExternalId, toExternalId, type, polarity, strength, evidenceLevel, confidence, lastValidated, owner',
  stakeholders: 'id, name, type, segment, owner, createdAt, updatedAt',
  behaviors: 'id, title, owner, createdAt, updatedAt',
  deliverables: 'id, title, owner, createdAt, updatedAt'
});

/**
 * Ensure initial migration of initiatives from StorageService -> IndexedDB.
 * Runs lazily on first import; safe to call multiple times.
 */
export async function migrateInitiativesIfEmpty() {
  const count = await db.table('initiatives').count();
  if (count > 0) return false;
  try {
    const primary = StorageService.get('initiatives', null);
    let data = Array.isArray(primary) ? primary : null;
    if (!data) {
      try { data = JSON.parse(localStorage.getItem('initiatives') || '[]'); } catch { data = []; }
    }
    if (Array.isArray(data) && data.length > 0) {
      // Ensure ids
      const rows = data.map((i) => ({
        id: (typeof i.id === 'string' && i.id) ? i.id : (i.id != null ? String(i.id) : `init_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`),
        ...i,
        createdAt: i.createdAt || new Date().toISOString(),
        updatedAt: i.updatedAt || new Date().toISOString()
      }));
      await db.transaction('rw', db.initiatives, async () => {
        await db.initiatives.clear();
        await db.initiatives.bulkPut(rows);
      });
      return true;
    }
  } catch (e) {
    console.warn('Dexie migration skipped:', e);
  }
  return false;
}

export default db;

// Dev helpers
if (import.meta.env?.DEV) {
  try { window.sipDb = db; } catch (_) {}
}
